// src/pages/Admin/CompanyHmoStaff.jsx
// Admin HMO Staff Management - Enhanced with Expired Staff Focus & Bulk Renewal
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Swal from 'sweetalert2';
import { authenticatedFetch } from '../../utils/api';

// ============================================================================
// CONFIGURATION & CONSTANTS
// ============================================================================
const API_BASE = process.env.REACT_APP_API_URL || 'https://fslapi.devformula.com/api';
const DEFAULT_MINIMUM_PREMIUM = 2700;
const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_xxxxxxxxxxxxxxxx';

// HMO Department configuration
const HMO_DEPARTMENTS = {
  'Medical': { color: 'danger', icon: 'fa-stethoscope' },
  'Nursing': { color: 'info', icon: 'fa-user-nurse' },
  'Administration': { color: 'primary', icon: 'fa-building' },
  'Pharmacy': { color: 'success', icon: 'fa-pills' },
  'Laboratory': { color: 'warning', icon: 'fa-flask' },
  'Records': { color: 'secondary', icon: 'fa-folder-open' },
  'Billing': { color: 'purple', icon: 'fa-file-invoice' },
  'Customer Care': { color: 'info', icon: 'fa-headset' },
};

// Payment periods (1-12 months)
const PAYMENT_PERIODS = Array.from({ length: 12 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `${i + 1} Month${i + 1 > 1 ? 's' : ''}`,
  months: i + 1
}));

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const formatCurrency = (amount, currency = 'NGN') => {
  const num = parseFloat(amount) || 0;
  const symbols = { NGN: '₦', USD: '$', EUR: '€', GBP: '£' };
  return `${symbols[currency] || '₦'}${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getStatusColor = (status) => {
  const colors = {
    active: 'success', inactive: 'secondary', suspended: 'warning', 
    terminated: 'danger', onLeave: 'info', expired: 'danger'
  };
  return colors[status?.toLowerCase()] || 'secondary';
};

const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const defaultOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-NG', { ...defaultOptions, ...options });
  } catch (e) {
    console.warn('Date format error:', dateString, e);
    return 'Invalid Date';
  }
};

// Calculate coverage status with real-time countdown
const calculateCoverageStatus = (coverageEndDate, coverageDays) => {
  if (!coverageEndDate) return { remainingDays: null, isExpired: false, isExpiringSoon: false, status: 'unknown', countdown: null };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(coverageEndDate);
  endDate.setHours(23, 59, 59, 999);
  
  const diffTime = endDate - today;
  const remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  const isExpired = remainingDays < 0 || (coverageDays !== undefined && coverageDays <= 0);
  const isExpiringSoon = !isExpired && remainingDays <= 30 && remainingDays >= 0;
  
  let countdown = null;
  if (!isExpired && remainingDays >= 0) {
    if (remainingDays > 0) {
      const hours = new Date(diffTime).getUTCHours();
      const minutes = new Date(diffTime).getUTCMinutes();
      countdown = `${remainingDays}d ${hours}h ${minutes}m`;
    } else {
      countdown = 'Expires today';
    }
  }
  
  const status = isExpired ? 'expired' : isExpiringSoon ? 'expiring' : 'active';
  return { remainingDays, isExpired, isExpiringSoon, status, countdown };
};

const generateReference = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let ref = 'HMO-COMP-';
  for (let i = 0; i < 10; i++) ref += chars.charAt(Math.floor(Math.random() * chars.length));
  return ref;
};

const getAuthHeaders = () => {
  // 🔍 Debug logging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔐 Auth Headers Check:', {
      hasAuthKey: !!process.env.REACT_APP_AUTH_KEY,
      hasToken: !!process.env.REACT_APP_API_TOKEN,
      hasSecret: !!process.env.REACT_APP_SECRET_KEY
    });
  }
  
  return {
    'AuthorizationKey': process.env.REACT_APP_AUTH_KEY || '',
    'Token': process.env.REACT_APP_API_TOKEN || '',
    'SecretKey': process.env.REACT_APP_SECRET_KEY || '',
    'Content-Type': 'application/json',
  };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function CompanyHmoStaff() {
  // ✅ EXTRACT BOTH companyId AND productId FROM URL PARAMS
  const { companyId, productId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);

  // Data State
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  
  // Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBulkRenewalModal, setShowBulkRenewalModal] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  // Form State
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', gender: '', department: '', companyId: companyId
  });
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [pendingStaffData, setPendingStaffData] = useState(null);

  // Payment State
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentReference, setPaymentReference] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Stats State
  const [staffStats, setStaffStats] = useState({
    totalStaff: 0, activeEnrollees: 0, monthlyPremium: 0, perStaffMonth: DEFAULT_MINIMUM_PREMIUM,
    expiredCount: 0, expiringSoonCount: 0, expiredStaff: []
  });

  // Bulk Renewal State
  const [renewalSelection, setRenewalSelection] = useState({
    mode: 'expired',
    selectedStaffIds: [],
    renewalPeriod: null,
    renewalPremium: 0
  });

  // Derived values
  const premiumPerStaff = useMemo(() => DEFAULT_MINIMUM_PREMIUM, []);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Debug: Log params on mount + validate productId
  useEffect(() => {
    console.log('🔍 Component Params:', { companyId, productId, hasProductId: !!productId });
    
    // ✅ Validate that productId exists when component mounts
    if (companyId && !productId) {
      console.warn('⚠️ productId is missing from URL params');
      // Optional: redirect or show error
      // toast.error('Product ID is required');
      // navigate('/admin/companies', { replace: true });
    }
  }, [companyId, productId, navigate]);

  // Load Paystack SDK
  useEffect(() => {
    if (window.PaystackPop) { setPaystackLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => { console.log('✅ Paystack loaded'); setPaystackLoaded(true); };
    script.onerror = () => { toast.error('Failed to load payment gateway'); setPaystackLoaded(false); };
    document.body.appendChild(script);
    return () => {
      const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
      if (existing) existing.remove();
    };
  }, []);

  // Initialize AOS & fetch data
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
    if (companyId) {
      fetchCompanyInfo();
      fetchCompanyStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Real-time countdown updater (every 60 seconds)
  useEffect(() => {
    if (staffList.length === 0) return;
    countdownIntervalRef.current = setInterval(() => setStaffList(prev => [...prev]), 60000);
    return () => { if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current); };
  }, [staffList.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current); };
  }, []);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchCompanyInfo = useCallback(async () => {
    try {
      // ✅ Build requestBody with conditional ProductId
      const requestBody = { companyId: parseInt(companyId) };
      if (productId) {
        requestBody.productId = parseInt(productId);
      }
      
      const response = await authenticatedFetch(
        `${API_BASE}/hmo/get-company`,
        'POST',
        requestBody,
        { headers: getAuthHeaders() }
      );
      if (response?.status === 'success' && response.data) {
        setCompanyInfo({
          businessName: response.data.businessName || 'Unknown Company',
          email: response.data.email || 'N/A',
          stateOfOperation: response.data.stateOfOperation || 'N/A',
        });
      }
    } catch (error) {
      console.error('💥 Fetch company error:', error);
      toast.error('Could not load company details');
    }
  }, [companyId, productId]);

  const fetchCompanyStaff = useCallback(async () => {
    if (!companyId) return;
    try {
      setStaffLoading(true);
      
      // ✅ Build requestBody with conditional ProductId
      const requestBody = { companyId: parseInt(companyId) };
      if (productId) {
        requestBody.productId = parseInt(productId);
      }
      
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/get-staff-by-company`,
        'POST',
        requestBody,
        { headers: getAuthHeaders() }
      );
      
      if (response?.success === true && response?.data?.staff) {
        const staff = response.data.staff.map(s => ({
          // ✅ USE 'id' AS PRIMARY KEY (fallback to staffId for backward compatibility)
          id: s.id || s.staffId,
          firstName: s.firstName,
          lastName: s.lastName,
          email: s.email,
          phone: s.phone,
          gender: s.gender,
          department: s.department,
          status: s.currentStatus || s.status || 'active',
          coverageStartDate: s.coverageStartDate,
          coverageEndDate: s.coverageEndDate,
          coverageDays: s.coverageDays || 0,
          periodMonths: s.periodMonths,
          premiumPerStaff: s.premiumPerStaff || DEFAULT_MINIMUM_PREMIUM,
          totalPremium: s.totalPremium,
          paymentStatus: s.paymentStatus,
          createdAt: s.createdAt,
        }));
        setStaffList(staff);
        
        // Calculate stats including expired staff list
        const expiredStaff = staff.filter(s => {
          const { isExpired } = calculateCoverageStatus(s.coverageEndDate, s.coverageDays);
          return isExpired;
        });
        const expiringSoonCount = staff.filter(s => {
          const { isExpiringSoon } = calculateCoverageStatus(s.coverageEndDate, s.coverageDays);
          return isExpiringSoon;
        }).length;
        
        if (response.data.summary) {
          setStaffStats({
            totalStaff: response.data.summary.totalStaff,
            activeEnrollees: response.data.summary.activeStaff,
            monthlyPremium: response.data.summary.totalPremium,
            perStaffMonth: response.data.summary.premiumPerStaff || DEFAULT_MINIMUM_PREMIUM,
            expiredCount: response.data.summary.expiredCount || expiredStaff.length,
            expiringSoonCount,
            expiredStaff
          });
        } else {
          setStaffStats({
            totalStaff: staff.length,
            activeEnrollees: staff.filter(s => s.status?.toLowerCase() === 'active').length,
            monthlyPremium: staff.length * DEFAULT_MINIMUM_PREMIUM,
            perStaffMonth: DEFAULT_MINIMUM_PREMIUM,
            expiredCount: expiredStaff.length,
            expiringSoonCount,
            expiredStaff
          });
        }
      } else {
        setStaffList([]);
        setStaffStats({ 
          totalStaff: 0, activeEnrollees: 0, monthlyPremium: 0, 
          perStaffMonth: DEFAULT_MINIMUM_PREMIUM, expiredCount: 0, expiringSoonCount: 0, expiredStaff: []
        });
        if (response?.message && !response.message.includes('No staff found')) {
          toast.warning(response.message);
        }
      }
    } catch (error) {
      console.error('💥 Fetch staff error:', error);
      setStaffList([]);
      setStaffStats({ 
        totalStaff: 0, activeEnrollees: 0, monthlyPremium: 0, 
        perStaffMonth: DEFAULT_MINIMUM_PREMIUM, expiredCount: 0, expiringSoonCount: 0, expiredStaff: []
      });
      if (error.message?.includes('401') || error.message?.includes('Authorization')) {
        toast.error('Authentication failed. Please log in again.');
        localStorage.removeItem('fsl_admin_session');
        navigate('/admin-login', { replace: true });
      } else {
        toast.error('Failed to load HMO staff. Please try again.');
      }
    } finally {
      setStaffLoading(false);
      setLoading(false);
    }
  }, [companyId, productId, navigate]);

  // ============================================================================
  // BUSINESS LOGIC
  // ============================================================================

  const handleAuthError = (error) => {
    const errorMsg = error?.message?.toLowerCase() || '';
    if (errorMsg.includes('401') || errorMsg.includes('expired') || errorMsg.includes('unauthorized') || errorMsg.includes('authorization')) {
      toast.error('Authentication failed. Please log in again.');
      localStorage.removeItem('fsl_admin_session');
      navigate('/admin-login', { replace: true });
      return true;
    }
    return false;
  };

  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    setPaymentAmount(DEFAULT_MINIMUM_PREMIUM * period.months);
    setShowPeriodDropdown(false);
  };

  const resetStaffForm = () => {
    setStaffForm({ firstName: '', lastName: '', email: '', phone: '', gender: '', department: '', companyId: companyId });
    setEditingStaff(null); setSelectedPeriod(null); setPaymentAmount(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openAddModal = () => { resetStaffForm(); setShowAddModal(true); };
  
  const closeModals = () => {
    setShowAddModal(false); setShowImportModal(false); setShowPaymentModal(false);
    setShowBulkRenewalModal(false); resetStaffForm();
    setImportFile(null); setImportPreview([]); setPendingStaffData(null);
    setSelectedPeriod(null);
    setRenewalSelection({ mode: 'expired', selectedStaffIds: [], renewalPeriod: null, renewalPremium: 0 });
  };

  // ============================================================================
  // PAYMENT HANDLERS - FIXED EMAIL LOGIC
  // ============================================================================

  const preparePremiumPayment = async (isImport = false, importCount = 1) => {
    if (!paystackLoaded) { toast.warning('Payment gateway is loading. Please wait...'); return false; }
    
    if (!isImport) {
      if (!staffForm.firstName || !staffForm.lastName || !staffForm.email || !staffForm.department) {
        toast.error('First name, last name, email, and department are required'); return false;
      }
      if (!selectedPeriod) { toast.error('Please select a coverage period'); return false; }
    } else {
      if (!importFile) { toast.error('Please select a file first'); return false; }
      if (!selectedPeriod) { toast.error('Please select a coverage period'); return false; }
      if (!pendingStaffData?.staffCount || pendingStaffData.staffCount <= 0) {
        toast.error('No valid staff records found'); return false;
      }
    }

    const calculatedAmount = isImport 
      ? (pendingStaffData.staffCount * DEFAULT_MINIMUM_PREMIUM * selectedPeriod.months)
      : (DEFAULT_MINIMUM_PREMIUM * selectedPeriod.months);
    
    const reference = generateReference();
    setPaymentAmount(calculatedAmount); setPaymentReference(reference);
    
    // ✅ CRITICAL: Store email explicitly for single staff enrollment
    setPendingStaffData(prev => ({ 
      ...prev, 
      paymentReference: reference, 
      premiumAmount: calculatedAmount,
      periodMonths: selectedPeriod.months, 
      periodLabel: selectedPeriod.label,
      isImport, 
      importCount: isImport ? pendingStaffData.staffCount : 1,
      email: isImport ? null : staffForm.email  // <-- ADD THIS LINE
    }));
    
    setShowPaymentModal(true); return true;
  };

  const openPaystackPopup = (reference, amount, email, onSuccess, onClose) => {
    return new Promise((resolve, reject) => {
      if (!window.PaystackPop) return reject(new Error('Paystack not loaded'));
      
      const config = {
        key: PAYSTACK_PUBLIC_KEY, 
        email: email, 
        amount: amount * 100, 
        currency: 'NGN', 
        ref: reference,
        meta: {
          company_id: companyId,
          product_id: productId,  // ✅ Include product_id in metadata (may be undefined)
          premium_type: 'hmo_staff_company',
          period_months: selectedPeriod?.months,
          applicant_name: pendingStaffData?.firstName + ' ' + pendingStaffData?.lastName,
          product_name: companyInfo?.businessName,
          is_bulk: pendingStaffData?.isImport || pendingStaffData?.isBulkRenewal || false,
          bulk_count: pendingStaffData?.importCount || renewalSelection?.selectedStaffIds?.length || 1
        },
        callback: (response) => { 
          console.log('✅ Paystack callback:', response); 
          resolve(response); 
          onSuccess?.(response); 
        },
        onClose: () => { 
          console.log('ℹ️ Paystack closed'); 
          reject(new Error('Payment cancelled')); 
          onClose?.(); 
        }
      };

      try { 
        window.PaystackPop.setup(config).openIframe(); 
      } catch (err) { 
        console.error('❌ Error opening Paystack:', err); 
        reject(err); 
      }
    });
  };

  const saveCompanyStaff = async (staffData, paymentData, isImport = false) => {
    try {
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + (paymentData.periodMonths || 1));
      endDate.setDate(endDate.getDate() - 1);

      // ✅ FIX: Conditionally include ProductId only when it has a valid value
      const payload = {
        CompanyId: parseInt(companyId),
        ...(productId && { ProductId: parseInt(productId) }),  // ✅ Only include if valid, never send null
        FirstName: staffData.firstName || null, 
        LastName: staffData.lastName || null,
        StaffEmail: staffData.email || null, 
        Phone: staffData.phone || null,
        Gender: staffData.gender || null, 
        Department: staffData.department,
        CoverageStartDate: startDate, 
        PeriodMonths: paymentData.periodMonths || 1,
        PaymentReference: paymentData.paymentReference,
        TransactionId: paymentData.transactionId || null,
        TotalPremium: paymentData.premiumAmount, 
        Currency: 'NGN', 
        PaymentStatus: 'success',
        IsImport: isImport, 
        ImportStaffList: isImport ? staffData.importList || [] : null
      };

      // 🔍 Debug payload (remove in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 Payload to /save-company-hmo-staff:', JSON.stringify(payload, null, 2));
        console.log('🔍 ProductId in payload:', payload.ProductId);
      }

      const response = await authenticatedFetch(
        `${API_BASE}/Settings/save-company-hmo-staff`, 
        'POST', 
        payload, 
        { headers: getAuthHeaders() }
      );

      if (response?.success) {
        const msg = isImport 
          ? `✅ Imported ${response.data?.importCount || 0} staff with coverage ${startDate} to ${endDate.toISOString().split('T')[0]}`
          : `✅ Staff enrolled with coverage ${startDate} to ${endDate.toISOString().split('T')[0]}`;
        toast.success(msg); 
        return response;
      } else throw new Error(response?.message || 'Failed to save staff');
    } catch (error) {
      console.error('💥 Save error:', error);
      toast.error(error.message || 'Failed to enroll staff');
      throw error;
    }
  };

  const handlePaystackSuccess = async (reference) => {
    try {
      setIsProcessingPayment(true);
      console.log('✅ Payment successful:', reference.reference);
      
      const staffData = { ...pendingStaffData, CoverageStartDate: new Date().toISOString().split('T')[0] };
      const paymentData = {
        paymentReference: reference.reference, 
        transactionId: reference.tranx_id || '',
        premiumAmount: paymentAmount, 
        periodMonths: selectedPeriod?.months || 1
      };
      const isImport = pendingStaffData?.file ? true : false;
      
      await saveCompanyStaff(staffData, paymentData, isImport);
      closeModals(); 
      fetchCompanyStaff();
    } catch (error) {
      console.error('💥 Enrollment error:', error);
      if (handleAuthError(error)) return;
      if (error.message?.includes('duplicate') || error.message?.includes('already exists')) {
        toast.warning('This staff member may already be enrolled.');
      } else toast.error(error.message || 'Enrollment failed. Please contact support.');
    } finally { 
      setIsProcessingPayment(false); 
    }
  };

  const handlePaystackClose = () => { 
    toast.info('Payment cancelled. Staff was not enrolled.'); 
    closeModals(); 
  };

  // ============================================================================
  // BULK RENEWAL - FIXED WITH RENEWALDATA ARRAY & ProductId
  // ============================================================================

  const openBulkRenewalModal = (mode = 'expired') => {
    let selectedIds = [];
    
    if (mode === 'expired') {
      selectedIds = staffStats.expiredStaff.map(s => s.id);
    } else if (mode === 'expiring') {
      selectedIds = staffList.filter(s => {
        const { isExpiringSoon } = calculateCoverageStatus(s.coverageEndDate, s.coverageDays);
        return isExpiringSoon;
      }).map(s => s.id);
    } else if (mode === 'all') {
      selectedIds = staffList.map(s => s.id);
    }
    
    setRenewalSelection({ mode, selectedStaffIds: selectedIds, renewalPeriod: null, renewalPremium: 0 });
    setShowBulkRenewalModal(true);
  };

  const handleRenewalModeChange = (mode) => {
    let selectedIds = [];
    if (mode === 'all') selectedIds = staffList.map(s => s.id);
    else if (mode === 'expired') selectedIds = staffStats.expiredStaff.map(s => s.id);
    else if (mode === 'expiring') {
      selectedIds = staffList.filter(s => {
        const { isExpiringSoon } = calculateCoverageStatus(s.coverageEndDate, s.coverageDays);
        return isExpiringSoon;
      }).map(s => s.id);
    }
    setRenewalSelection(prev => ({ ...prev, mode, selectedStaffIds: selectedIds }));
  };

  const handleStaffSelectionToggle = (id) => {
    setRenewalSelection(prev => {
      const isSelected = prev.selectedStaffIds.includes(id);
      const newSelected = isSelected 
        ? prev.selectedStaffIds.filter(staffId => staffId !== id)
        : [...prev.selectedStaffIds, id];
      return { ...prev, selectedStaffIds: newSelected };
    });
  };

  const handleRenewalPeriodSelect = (period) => {
    const staffCount = renewalSelection.selectedStaffIds.length;
    const totalPremium = staffCount * DEFAULT_MINIMUM_PREMIUM * period.months;
    setRenewalSelection(prev => ({ ...prev, renewalPeriod: period, renewalPremium: totalPremium }));
  };

  const prepareBulkRenewalPayment = async () => {
    if (renewalSelection.selectedStaffIds.length === 0) { toast.error('Please select at least one staff member to renew'); return; }
    if (!renewalSelection.renewalPeriod) { toast.error('Please select a renewal period'); return; }
    if (!paystackLoaded) { toast.warning('Payment gateway is loading. Please wait...'); return; }

    const reference = generateReference();
    setPaymentReference(reference); setPaymentAmount(renewalSelection.renewalPremium);
    
    setPendingStaffData({
      paymentReference: reference, 
      premiumAmount: renewalSelection.renewalPremium,
      periodMonths: renewalSelection.renewalPeriod.months, 
      periodLabel: renewalSelection.renewalPeriod.label,
      isBulkRenewal: true, 
      staffIdsToRenew: renewalSelection.selectedStaffIds,
      // Use company email for bulk renewals
      email: companyInfo?.email || 'bulk-renewal@company.com'
    });
    
    setShowBulkRenewalModal(false); setShowPaymentModal(true);
  };

  const executeBulkRenewal = async (paymentReference, transactionId) => {
    try {
      const startDate = new Date().toISOString().split('T')[0];
      
      // ✅ BUILD RenewalData ARRAY WITH 'Id' PROPERTY (matches backend expectation)
      const renewalData = renewalSelection.selectedStaffIds.map(id => ({
        Id: parseInt(id),
        PremiumAmount: DEFAULT_MINIMUM_PREMIUM * renewalSelection.renewalPeriod.months
      }));
      
      // ✅ FIX: Conditionally include ProductId only when it has a valid value
      const payload = {
        CompanyId: parseInt(companyId),
        ...(productId && { ProductId: parseInt(productId) }),  // ✅ Only include if valid, never send null
        RenewalData: renewalData,  // ✅ Array of objects, NOT StaffIds array
        CoverageStartDate: startDate,
        PeriodMonths: renewalSelection.renewalPeriod.months,
        PaymentReference: paymentReference,
        TransactionId: transactionId,
        TotalPremium: renewalSelection.renewalPremium,
        Currency: 'NGN',
        PaymentStatus: 'success',
        RenewalType: renewalSelection.mode
      };

      // 🔍 Debug payload (remove in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 Bulk Renewal Payload:', JSON.stringify(payload, null, 2));
        console.log('🔍 ProductId in bulk payload:', payload.ProductId);
      }

      const response = await authenticatedFetch(
        `${API_BASE}/Settings/bulk-renew-hmo-staff`, 
        'POST', 
        payload, 
        { headers: getAuthHeaders() }
      );

      if (response?.success) {
        toast.success(`✅ Successfully renewed ${response.data?.renewedCount || renewalSelection.selectedStaffIds.length} staff members`);
        fetchCompanyStaff(); return true;
      } else throw new Error(response?.message || 'Bulk renewal failed');
    } catch (error) {
      console.error('💥 Bulk renewal error:', error);
      if (handleAuthError(error)) return false;
      toast.error(error.message || 'Failed to renew staff coverage. Please contact support.');
      return false;
    }
  };

  // ============================================================================
  // IMPORT FUNCTIONALITY
  // ============================================================================

  const downloadSampleCSV = () => {
    const headers = ['firstName', 'lastName', 'email', 'phone', 'gender', 'department'];
    const sampleData = [
      ['John', 'Doe', 'john@example.com', '08012345678', 'male', 'Medical'],
      ['Jane', 'Smith', 'jane@example.com', '08098765432', 'female', 'Nursing'],
      ['Michael', 'Brown', 'michael@example.com', '08123456789', 'male', 'Administration']
    ];
    const csvContent = [headers.join(','), ...sampleData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob); link.download = 'hmo_staff_sample.csv'; link.click();
    toast.success('Sample CSV downloaded!');
  };

  const parseImportFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const staffCount = Math.max(0, lines.length - 1);
      if (staffCount <= 0) { toast.error('No valid records found'); return; }
      setPendingStaffData(prev => ({ ...prev, file, staffCount, premiumPerStaff: DEFAULT_MINIMUM_PREMIUM }));
      const preview = lines.slice(0, 6).map(line => line.split(',').map(c => c.trim()));
      setImportPreview(preview);
      toast.info(`✅ File parsed: ${staffCount} staff records. Select period to continue.`);
    };
    reader.readAsText(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type)) { toast.error('Please upload CSV or Excel file'); return; }
    setImportFile(file); parseImportFile(file);
  };

  const handleImportProceed = async () => {
    if (!selectedPeriod) { toast.error('Please select coverage period'); return; }
    const success = await preparePremiumPayment(true);
    if (success) { /* Payment modal opens */ }
  };

  const handleImportAfterPayment = async (reference) => {
    try {
      setIsProcessingPayment(true);
      console.log('✅ Payment successful for import:', reference.reference);
      
      const importStaffList = [];
      const fileText = await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = (e) => resolve(e.target.result);
        fr.readAsText(pendingStaffData.file);
      });
      
      const lines = fileText.split('\n').filter(line => line.trim());
      if (lines.length < 2) throw new Error('Invalid CSV format');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const staff = {};
        headers.forEach((header, idx) => { staff[header] = values[idx] || ''; });
        if (staff.firstname && staff.lastname && staff.email && staff.department) {
          importStaffList.push({
            FirstName: staff.firstname.trim(), LastName: staff.lastname.trim(),
            Email: staff.email.toLowerCase().trim(),
            Phone: staff.phone ? staff.phone.replace(/\D/g, '') : null,
            Gender: staff.gender || null, Department: staff.department.trim()
          });
        }
      }
      
      if (importStaffList.length === 0) throw new Error('No valid staff records found in CSV file');

      const staffData = { CoverageStartDate: new Date().toISOString().split('T')[0], importList: importStaffList };
      const paymentData = {
        paymentReference: reference.reference, transactionId: reference.tranx_id || '',
        premiumAmount: paymentAmount, periodMonths: pendingStaffData.periodMonths || 1
      };

      await saveCompanyStaff(staffData, paymentData, true);
      closeModals(); fetchCompanyStaff();
    } catch (error) {
      console.error('💥 Import enrollment error:', error);
      if (handleAuthError(error)) return;
      toast.error(error.message || 'Payment successful but import failed. Please contact support.');
    } finally { setIsProcessingPayment(false); }
  };

  // ============================================================================
  // STAFF MANAGEMENT
  // ============================================================================

  const handleDeleteStaff = async (id, staffName) => {
    const result = await Swal.fire({
      title: 'Remove HMO Staff?',
      text: `Are you sure you want to remove ${staffName} from this company?`,
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, Remove',
      cancelButtonText: 'Cancel', confirmButtonColor: '#dc3545',
    });
    
    if (result.isConfirmed) {
      try {
        // ✅ Build requestBody with conditional ProductId
        const requestBody = { id, companyId: parseInt(companyId) };
        if (productId) {
          requestBody.productId = parseInt(productId);
        }
        
        const res = await authenticatedFetch(`${API_BASE}/Settings/remove-company-staff`, 'POST',
          requestBody, 
          { headers: getAuthHeaders() }
        );
        if (res?.success) { toast.success('Staff removed successfully'); fetchCompanyStaff(); }
        else toast.error(res?.message || 'Remove failed');
      } catch (error) { console.error('Delete error:', error); handleAuthError(error); toast.error('Failed to remove staff'); }
    }
  };

  const handleRenewSingleStaff = async (staff) => {
    setRenewalSelection({ mode: 'selected', selectedStaffIds: [staff.id], renewalPeriod: null, renewalPremium: 0 });
    setShowBulkRenewalModal(true);
    toast.info('Select renewal period and proceed to payment');
  };

  // ✅ Filter staff with expired-only toggle
  const filteredStaff = useMemo(() => {
    let result = staffList.filter(staff => {
      const search = searchTerm.toLowerCase();
      const matchSearch = 
        staff.firstName?.toLowerCase().includes(search) ||
        staff.lastName?.toLowerCase().includes(search) ||
        staff.email?.toLowerCase().includes(search) ||
        staff.department?.toLowerCase().includes(search);
      const matchDept = !filterDepartment || staff.department === filterDepartment;
      const matchStatus = !filterStatus || staff.status?.toLowerCase() === filterStatus.toLowerCase();
      return matchSearch && matchDept && matchStatus;
    });
    
    // ✅ Apply expired-only filter
    if (showExpiredOnly) {
      result = result.filter(staff => {
        const { isExpired } = calculateCoverageStatus(staff.coverageEndDate, staff.coverageDays);
        return isExpired;
      });
    }
    
    return result;
  }, [staffList, searchTerm, filterDepartment, filterStatus, showExpiredOnly]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderPeriodOptions = () => PAYMENT_PERIODS.map(period => (
    <div key={period.value} onClick={() => handlePeriodSelect(period)} className="p-3 period-option"
      style={{ cursor: 'pointer', backgroundColor: selectedPeriod?.value === period.value ? '#FFF8F0' : 'white', 
        color: selectedPeriod?.value === period.value ? '#F89423' : '#111827', borderBottom: '1px solid #f3f4f6' }}>
      {period.label} - {formatCurrency(DEFAULT_MINIMUM_PREMIUM * period.months)}
    </div>
  ));

  // Navigation helpers
  const handleBack = () => navigate('/admin/companies');
  const handleNavClick = (path, e) => { e?.preventDefault(); if (path) navigate(path); };
  const handleLogout = () => {
    localStorage.removeItem('fsl_admin_session');
    toast.success('Logged out');
    navigate('/admin-login', { replace: true });
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (loading) {
    return (
      <div className="admin-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
          <p className="text-muted">Loading HMO staff dashboard...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="admin-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/admin" className="brand-logo">
            <img src="/images/fsl_logo.png" alt="FSL Admin" style={{ height: '45px' }} />
            <span style={{ marginLeft: '8px', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>Admin</span>
          </Link>
        </div>
        <nav className="sidebar-nav">
          {[
            { section: 'MAIN', items: [{ name: 'Dashboard', icon: 'fa-chart-line', path: '/admin' }] },
            { section: 'MANAGEMENT', items: [
                
               
                { name: 'Clients', icon: 'fa-building', path: '/admin/companies', active: true },
                { name: 'Settings', icon: 'fa-cog', path: '/admin/crud/title' }
              ]}

              
          ].map((section, idx) => (
            <div key={idx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item) => (
                  <li key={item.name}>
                    <Link to={item.path} className={`nav-link ${item.path === window.location.pathname ? 'active' : ''}`}
                      onClick={(e) => handleNavClick(item.path, e)}>
                      <i className={`fas ${item.icon} me-2`}></i>{item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              {/* <li><Link to="/admin/profile" className="nav-link"><i className="fas fa-user me-2"></i>Profile</Link></li> */}
              <li><a href="/" className="nav-link" onClick={handleLogout}><i className="fas fa-sign-out-alt me-2"></i>Logout</a></li>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="page-title">
              <button className="btn btn-link p-0 me-2" onClick={handleBack} title="Back to Companies">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h4 className="mb-0 d-inline" style={{ color: '#1f2937', fontWeight: '600' }}>
                HMO Staff • {companyInfo?.businessName || 'Loading...'}
              </h4>
            </div>
          </div>
          <div className="header-right">
            <button className="header-icon"><i className="fas fa-search"></i></button>
            <button className="header-icon"><i className="fas fa-bell"></i><span className="badge">2</span></button>
            <div className="user-avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #F89423 0%, #F89423 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', cursor: 'pointer' }}>AD</div>
          </div>
        </header>

        <div className="content-wrapper" style={{ padding: '30px' }}>
          
          {/* ✅ EXPIRED STAFF ALERT BANNER - PROMINENT */}
          {staffStats.expiredCount > 0 && (
            <div className="alert alert-danger border-0 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2" data-aos="fade-up" style={{ backgroundColor: '#fff5f5', borderLeft: '4px solid #dc3545', animation: 'pulse 2s infinite' }}>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-danger text-white rounded-circle p-2"><i className="fas fa-exclamation-triangle"></i></div>
                <div>
                  <h6 className="mb-1 text-danger fw-bold">⚠️ {staffStats.expiredCount} Staff Coverage Expired</h6>
                  <p className="mb-0 small text-muted">These staff members no longer have active HMO coverage. Renew now to restore benefits.</p>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-danger btn-sm" onClick={() => { setShowExpiredOnly(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  <i className="fas fa-filter me-1"></i>View Expired Only
                </button>
                <button className="btn btn-warning btn-sm" onClick={() => openBulkRenewalModal('expired')}>
                  <i className="fas fa-sync-alt me-1"></i>Renew All Expired
                </button>
              </div>
            </div>
          )}

          {/* Expiring Soon Banner */}
          {staffStats.expiringSoonCount > 0 && !showExpiredOnly && (
            <div className="alert alert-warning border-0 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2" data-aos="fade-up">
              <div>
                <i className="fas fa-clock me-2"></i>
                <strong>{staffStats.expiringSoonCount} staff expiring soon</strong> within 30 days
              </div>
              <button className="btn btn-sm btn-outline-warning" onClick={() => openBulkRenewalModal('expiring')}>
                <i className="fas fa-sync-alt me-1"></i>Renew Expiring
              </button>
            </div>
          )}

          {/* ✅ STATS CARDS WITH EXPIRY COUNTS */}
          <div className="row g-3 mb-4" data-aos="fade-up">
            <div className="col-md-3 col-6">
              <div className="card border-0 bg-primary bg-opacity-10 h-100">
                <div className="card-body py-3 px-3 text-center">
                  <div className="bg-primary text-white rounded-circle p-2 d-inline-flex mb-2"><i className="fas fa-user-md fa-sm"></i></div>
                  <small className="text-muted d-block">Total HMO Staff</small>
                  <h3 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>{staffStats.totalStaff}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="card border-0 bg-success bg-opacity-10 h-100">
                <div className="card-body py-3 px-3 text-center">
                  <div className="bg-success text-white rounded-circle p-2 d-inline-flex mb-2"><i className="fas fa-check-circle fa-sm"></i></div>
                  <small className="text-muted d-block">Active Enrollees</small>
                  <h3 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>{staffStats.activeEnrollees}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="card border-0 bg-warning bg-opacity-10 h-100">
                <div className="card-body py-3 px-3 text-center">
                  <div className="bg-warning text-white rounded-circle p-2 d-inline-flex mb-2"><i className="fas fa-money-bill-wave fa-sm"></i></div>
                  <small className="text-muted d-block">Monthly Premium</small>
                  <h3 className="mb-0 fw-bold text-success" style={{ fontSize: '1.5rem' }}>{formatCurrency(staffStats.monthlyPremium)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="card border-0 bg-info bg-opacity-10 h-100">
                <div className="card-body py-3 px-3 text-center">
                  <div className="bg-info text-white rounded-circle p-2 d-inline-flex mb-2"><i className="fas fa-calculator fa-sm"></i></div>
                  <small className="text-muted d-block">Per Staff/Month</small>
                  <h3 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>{formatCurrency(staffStats.perStaffMonth)}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Department Filters */}
          {Object.keys(HMO_DEPARTMENTS).some(dept => staffList.some(s => s.department === dept)) && (
            <div className="mb-4" data-aos="fade-up">
              <h6 className="mb-3">📊 By Department</h6>
              <div className="d-flex flex-wrap gap-2">
                {Object.entries(HMO_DEPARTMENTS).map(([dept, config]) => {
                  const count = staffList.filter(s => s.department === dept).length;
                  if (count === 0) return null;
                  return (
                    <button 
                      key={dept} 
                      className={`btn btn-sm btn-outline-${config.color} d-flex align-items-center gap-2 ${filterDepartment === dept ? 'active' : ''}`} 
                      onClick={() => setFilterDepartment(filterDepartment === dept ? '' : dept)}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className={`fas ${config.icon}`}></i>
                      <span>{dept}</span>
                      <span className="badge bg-light text-dark">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search & Filters */}
          <div className="row g-3 mb-4" data-aos="fade-up">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0"><i className="fas fa-search text-muted"></i></span>
                <input type="text" className="form-control border-start-0" placeholder="Search by name, email, department..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                {searchTerm && <button className="btn btn-outline-secondary" onClick={() => setSearchTerm('')}><i className="fas fa-times"></i></button>}
              </div>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}>
                <option value="">All Departments</option>
                {Object.keys(HMO_DEPARTMENTS).map(dept => <option key={dept} value={dept}>{dept}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="onLeave">On Leave</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex gap-2 mb-4" data-aos="fade-up">
            {/* ✅ Expired-Only Toggle Button */}
            <button 
              className={`btn btn-sm ${showExpiredOnly ? 'btn-danger' : 'btn-outline-danger'}`} 
              onClick={() => setShowExpiredOnly(!showExpiredOnly)}
              title={showExpiredOnly ? 'Show all staff' : 'Show only expired staff'}
            >
              <i className={`fas ${showExpiredOnly ? 'fa-eye' : 'fa-exclamation-triangle'} me-1`}></i>
              {showExpiredOnly ? 'Show All' : `Expired (${staffStats.expiredCount})`}
            </button>
            
            <button className="btn btn-outline-success" onClick={() => setShowImportModal(true)}><i className="fas fa-file-upload me-2"></i>Upload CSV</button>
            <button className="btn btn-outline-primary" onClick={downloadSampleCSV}><i className="fas fa-download me-2"></i>Download Sample</button>
            <button className="btn btn-primary" onClick={openAddModal}><i className="fas fa-plus me-2"></i>Add HMO Staff & Pay</button>
          </div>

          {/* ✅ Staff Data Table WITH BULK SELECTION & COVERAGE COUNTDOWN */}
          <div className="card" data-aos="fade-up">
            <div className="card-body p-0">
              <div className="table-responsive">
                {staffLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-orange" role="status"></div>
                    <p className="text-muted mt-2">Loading staff records...</p>
                  </div>
                ) : filteredStaff.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-user-md fa-3x mb-3 opacity-25"></i>
                    <h5>No HMO staff found</h5>
                    <p className="mb-0">
                      {showExpiredOnly 
                        ? 'No expired staff found - all coverage is active!' 
                        : searchTerm ? 'Try different search terms' : 'Add staff to this company to get started'}
                    </p>
                  </div>
                ) : (
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="py-3 px-4">
                          <input 
                            type="checkbox" 
                            className="form-check-input"
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setRenewalSelection(prev => ({
                                ...prev,
                                selectedStaffIds: checked ? filteredStaff.map(s => s.id) : []
                              }));
                            }}
                            checked={renewalSelection.selectedStaffIds.length === filteredStaff.length && filteredStaff.length > 0}
                            title="Select all for bulk renewal"
                          />
                        </th>
                        <th className="py-3 px-4">Staff Member</th>
                        <th className="py-3 px-4">Department</th>
                        <th className="py-3 px-4">Contact</th>
                        <th className="py-3 px-4" style={{ minWidth: '260px' }}>
                          <i className="fas fa-calendar-alt me-1"></i>Coverage Period
                        </th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-end">Premium</th>
                        <th className="py-3 px-4 text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaff.map((staff) => {
                        const deptConfig = HMO_DEPARTMENTS[staff.department] || { color: 'secondary', icon: 'fa-building' };
                        const { remainingDays, isExpired, isExpiringSoon, countdown } = 
                          calculateCoverageStatus(staff.coverageEndDate, staff.coverageDays);
                        const startDate = formatDate(staff.coverageStartDate);
                        const endDate = formatDate(staff.coverageEndDate);
                        const coverageDays = staff.coverageDays || 0;
                        const isSelected = renewalSelection.selectedStaffIds.includes(staff.id);

                        return (
                          <tr 
                            key={staff.id} 
                            className={isExpired ? 'table-danger' : isExpiringSoon ? 'table-warning' : ''}
                            style={{ transition: 'background-color 0.3s ease' }}
                          >
                            {/* Selection Checkbox */}
                            <td className="py-3 px-4">
                              <input 
                                type="checkbox" 
                                className="form-check-input"
                                checked={isSelected}
                                onChange={() => handleStaffSelectionToggle(staff.id)}
                                title="Select for bulk renewal"
                              />
                            </td>
                            
                            {/* Staff Name */}
                            <td className="py-3 px-4">
                              <div className="d-flex align-items-center gap-3">
                                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: '600' }}>
                                  {staff.firstName?.[0]}{staff.lastName?.[0]}
                                </div>
                                <div>
                                  <div className="fw-semibold">{staff.firstName} {staff.lastName}</div>
                                  <small className="text-muted d-none d-md-block">{staff.email}</small>
                                </div>
                              </div>
                            </td>
                            
                            {/* Department */}
                            <td className="py-3 px-4">
                              <span className={`badge bg-${deptConfig.color} bg-opacity-10 text-dark border border-${deptConfig.color}`}>
                                <i className={`fas fa-${deptConfig.icon} me-1`}></i>
                                {staff.department || '-'}
                              </span>
                            </td>
                            
                            {/* Contact Info */}
                            <td className="py-3 px-4">
                              <small className="d-block">{staff.phone || 'N/A'}</small>
                              <small className="text-muted">{staff.gender || 'N/A'}</small>
                            </td>
                            
                            {/* ✅ Coverage Period with Real-time Countdown */}
                            <td className="py-3 px-4">
                              <div className="coverage-card p-2 rounded" style={{ 
                                backgroundColor: '#f8f9fa', 
                                borderLeft: `4px solid ${isExpired ? '#dc3545' : isExpiringSoon ? '#ffc107' : '#198754'}` 
                              }}>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <i className="fas fa-play-circle text-success" style={{ fontSize: '10px' }} title="Coverage Start"></i>
                                  <small className="text-muted">Start:</small>
                                  <span className="fw-semibold small" title={staff.coverageStartDate}>{startDate}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <i className="fas fa-stop-circle text-danger" style={{ fontSize: '10px' }} title="Coverage End"></i>
                                  <small className="text-muted">End:</small>
                                  <span className="fw-semibold small" title={staff.coverageEndDate}>{endDate}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                  <span className={`badge ${isExpired ? 'bg-danger' : isExpiringSoon ? 'bg-warning text-dark' : 'bg-success'}`}>
                                    <i className="fas fa-clock me-1"></i>
                                    {coverageDays} days total
                                  </span>
                                  {countdown && (
                                    <small className={`fw-semibold ${isExpired ? 'text-danger' : isExpiringSoon ? 'text-warning' : 'text-success'}`}>
                                      {isExpired ? `❌ Expired` : `⏱ ${countdown}`}
                                    </small>
                                  )}
                                </div>
                                {isExpiringSoon && !isExpired && (
                                  <small className="text-warning d-block mt-1">
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    Renew soon to avoid lapse
                                  </small>
                                )}
                              </div>
                            </td>
                            
                            {/* Status */}
                            <td className="py-3 px-4">
                              <span className={`badge bg-${isExpired ? 'danger' : getStatusColor(staff.status)}`}>
                                {isExpired ? '❌ Expired' : staff.status?.charAt(0).toUpperCase() + staff.status?.slice(1)}
                              </span>
                            </td>
                            
                            {/* Premium */}
                            <td className="py-3 px-4 text-end">
                              <div className="fw-semibold text-success">{formatCurrency(staff.totalPremium || staff.premiumPerStaff)}</div>
                              <small className="text-muted">{staff.paymentStatus === 'success' ? '✓ Paid' : 'Pending'}</small>
                            </td>
                            
                            {/* Actions */}
                            <td className="py-3 px-4 text-end">
                              <div className="btn-group btn-group-sm">
                                <button className="btn btn-sm btn-outline-primary" title="View Details">
                                  <i className="fas fa-eye"></i>
                                </button>
                                {isExpired && (
                                  <button className="btn btn-sm btn-outline-warning" title="Renew Coverage" onClick={() => handleRenewSingleStaff(staff)}>
                                    <i className="fas fa-sync-alt"></i>
                                  </button>
                                )}
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteStaff(staff.id, `${staff.firstName} ${staff.lastName}`)} title="Remove">
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan="6" className="fw-bold text-end py-3 px-4">TOTAL MONTHLY PREMIUM:</td>
                        <td className="fw-bold text-success py-3 px-4 text-end">
                          {formatCurrency(filteredStaff.reduce((sum, s) => sum + (s.totalPremium || s.premiumPerStaff || 0), 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {renewalSelection.selectedStaffIds.length > 0 && (
            <div className="mt-3 p-3 bg-light rounded d-flex align-items-center justify-content-between" data-aos="fade-up">
              <span className="text-muted">
                <i className="fas fa-users me-2"></i>
                {renewalSelection.selectedStaffIds.length} staff selected for renewal
              </span>
              <button className="btn btn-warning btn-sm" onClick={openBulkRenewalModal}>
                <i className="fas fa-sync-alt me-2"></i>Renew Selected ({renewalSelection.selectedStaffIds.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Staff Modal */}
      {showAddModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={(e) => { e.preventDefault(); preparePremiumPayment(); }}>
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">{editingStaff ? '✏️ Edit Staff' : '➕ Enrol HMO Staff'}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6"><label className="form-label small fw-semibold">First Name <span className="text-danger">*</span></label><input type="text" className="form-control" name="firstName" value={staffForm.firstName} onChange={(e) => setStaffForm(prev => ({ ...prev, firstName: e.target.value }))} required /></div>
                    <div className="col-md-6"><label className="form-label small fw-semibold">Last Name <span className="text-danger">*</span></label><input type="text" className="form-control" name="lastName" value={staffForm.lastName} onChange={(e) => setStaffForm(prev => ({ ...prev, lastName: e.target.value }))} required /></div>
                    <div className="col-md-6"><label className="form-label small fw-semibold">Email <span className="text-danger">*</span></label><input type="email" className="form-control" name="email" value={staffForm.email} onChange={(e) => setStaffForm(prev => ({ ...prev, email: e.target.value }))} required /></div>
                    <div className="col-md-6"><label className="form-label small fw-semibold">Phone</label><input type="tel" className="form-control" name="phone" value={staffForm.phone} onChange={(e) => setStaffForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="08012345678" /></div>
                    <div className="col-md-6"><label className="form-label small fw-semibold">Gender</label><select className="form-select" name="gender" value={staffForm.gender} onChange={(e) => setStaffForm(prev => ({ ...prev, gender: e.target.value }))}><option value="">Select</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select></div>
                    <div className="col-md-6"><label className="form-label small fw-semibold">Department <span className="text-danger">*</span></label><select className="form-select" name="department" value={staffForm.department} onChange={(e) => setStaffForm(prev => ({ ...prev, department: e.target.value }))} required><option value="">Select</option>{Object.keys(HMO_DEPARTMENTS).map(dept => <option key={dept} value={dept}>{dept}</option>)}</select></div>
                  </div>
                  <h6 className="border-bottom pb-2 mb-3 mt-4">📅 Coverage Period</h6>
                  <div className="mb-3 period-dropdown">
                    <label className="form-label small fw-semibold">Select Period <span className="text-danger">*</span></label>
                    <div className="position-relative">
                      <div onClick={() => setShowPeriodDropdown(!showPeriodDropdown)} className="form-control" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: selectedPeriod ? '#111827' : '#6b7280' }}>{selectedPeriod ? selectedPeriod.label : 'Select payment plan'}</span>
                        <i className={`fas fa-chevron-${showPeriodDropdown ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                      </div>
                      {showPeriodDropdown && (<div className="position-absolute w-100 mt-1 rounded-3 shadow" style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>{renderPeriodOptions()}</div>)}
                    </div>
                    {selectedPeriod && <small className="text-muted d-block mt-1">₦{DEFAULT_MINIMUM_PREMIUM.toLocaleString()} × {selectedPeriod.months} mo = {formatCurrency(DEFAULT_MINIMUM_PREMIUM * selectedPeriod.months)}</small>}
                  </div>
                  <div className="alert alert-info border-0 mt-3" style={{ backgroundColor: '#f0f9ff' }}>
                    <h6 className="mb-2">💰 Premium Summary</h6>
                    <div className="row small"><div className="col-6"><span className="text-muted">Per Month:</span> <span className="fw-semibold ms-2">₦{DEFAULT_MINIMUM_PREMIUM.toLocaleString()}</span></div><div className="col-6"><span className="text-muted">Total ({selectedPeriod?.label || '1 Month'}):</span> <span className="fw-semibold ms-2 text-success">{selectedPeriod ? formatCurrency(DEFAULT_MINIMUM_PREMIUM * selectedPeriod.months) : '₦0'}</span></div></div>
                  </div>
                </div>
                <div className="modal-footer border-top">
                  <button type="button" className="btn btn-secondary" onClick={closeModals}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={!selectedPeriod || !paystackLoaded}>
                    {!paystackLoaded ? 'Loading Payment...' : !selectedPeriod ? 'Select Period' : <><i className="fas fa-lock me-2"></i>Pay {formatCurrency(paymentAmount || DEFAULT_MINIMUM_PREMIUM)} & Enrol</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header border-bottom"><h5 className="modal-title">📥 Import HMO Staff from CSV</h5><button type="button" className="btn-close" onClick={closeModals}></button></div>
              <div className="modal-body">
                <div className="alert alert-info border-0 mb-4">
                  <h6 className="mb-2">📋 CSV Format</h6>
                  <p className="small mb-2"><strong>Columns:</strong> firstName, lastName, email, phone, gender, department</p>
                  <ul className="small mb-2"><li><strong>department:</strong> Medical, Nursing, Administration, Pharmacy, Laboratory, Records, Billing, Customer Care</li></ul>
                  <p className="small mb-0"><strong>Premium:</strong> ₦{DEFAULT_MINIMUM_PREMIUM.toLocaleString()}/staff/month</p>
                </div>
                <div className="d-flex gap-2 mb-4">
                  <button className="btn btn-outline-primary" onClick={downloadSampleCSV}><i className="fas fa-download me-2"></i>Download Sample</button>
                  <button className="btn btn-outline-secondary" onClick={() => fileInputRef.current?.click()}><i className="fas fa-upload me-2"></i>Select CSV</button>
                  <input type="file" ref={fileInputRef} className="d-none" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} />
                </div>
                {importFile && (<div className="border rounded p-3 mb-3 bg-light"><div className="d-flex justify-content-between align-items-center"><div><i className="fas fa-file-csv text-success me-2"></i><span className="fw-semibold">{importFile.name}</span><small className="text-muted d-block">{(importFile.size / 1024).toFixed(1)} KB</small></div><button className="btn btn-sm btn-outline-danger" onClick={() => { setImportFile(null); setImportPreview([]); }}><i className="fas fa-times"></i> Remove</button></div></div>)}
                {importPreview.length > 0 && (<div className="mt-3"><h6 className="mb-2">🔍 Preview</h6><div className="table-responsive" style={{ maxHeight: '200px', overflow: 'auto' }}><table className="table table-sm table-bordered small"><thead className="table-light"><tr>{importPreview[0]?.map((cell, i) => <th key={i}>{cell}</th>)}</tr></thead><tbody>{importPreview.slice(1).map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody></table></div></div>)}
                <h6 className="border-bottom pb-2 mb-3 mt-4">📅 Select Coverage Period</h6>
                <div className="mb-3 period-dropdown">
                  <label className="form-label small fw-semibold">Coverage Period <span className="text-danger">*</span></label>
                  <div className="position-relative">
                    <div onClick={() => setShowPeriodDropdown(!showPeriodDropdown)} className="form-control" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: selectedPeriod ? '#111827' : '#6b7280' }}>{selectedPeriod ? selectedPeriod.label : 'Select payment plan'}</span>
                      <i className={`fas fa-chevron-${showPeriodDropdown ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                    </div>
                    {showPeriodDropdown && (<div className="position-absolute w-100 mt-1 rounded-3 shadow" style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>{renderPeriodOptions()}</div>)}
                  </div>
                </div>
                {selectedPeriod && pendingStaffData?.staffCount > 0 && (<div className="alert alert-info border-0 mt-3" style={{ backgroundColor: '#f0f9ff' }}><h6 className="mb-2">💰 Import Summary</h6><div className="row small"><div className="col-6"><span className="text-muted">Staff Count:</span> <span className="fw-semibold ms-2">{pendingStaffData.staffCount}</span></div><div className="col-6"><span className="text-muted">Period:</span> <span className="fw-semibold ms-2">{selectedPeriod.label}</span></div><div className="col-6 mt-2"><span className="text-muted">Per Staff/Month:</span> <span className="fw-semibold ms-2">₦{DEFAULT_MINIMUM_PREMIUM.toLocaleString()}</span></div><div className="col-6 mt-2"><span className="text-muted">Total Premium:</span> <span className="fw-bold ms-2 text-success">{formatCurrency(pendingStaffData.staffCount * DEFAULT_MINIMUM_PREMIUM * selectedPeriod.months)}</span></div></div></div>)}
              </div>
              <div className="modal-footer border-top">
                <button type="button" className="btn btn-secondary" onClick={closeModals}>Cancel</button>
                <button className="btn btn-success" onClick={handleImportProceed} disabled={!importFile || !selectedPeriod || !pendingStaffData?.staffCount}><i className="fas fa-credit-card me-2"></i>Proceed to Payment</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal (Paystack) - FIXED EMAIL LOGIC */}
      {showPaymentModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom"><h5 className="modal-title">💳 Confirm HMO Premium Payment</h5><button type="button" className="btn-close" onClick={closeModals}></button></div>
              <div className="modal-body text-center">
                <div className="mb-4"><i className="fas fa-heartbeat text-primary fa-4x mb-3"></i><h5>Pay HMO Premium via Paystack</h5><p className="text-muted">Secure payment for staff enrolment</p></div>
                <div className="card bg-light border-0 mb-4">
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2"><span className="text-muted">Staff Count:</span><span className="fw-bold">{pendingStaffData?.importCount || pendingStaffData?.staffCount || 1}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span className="text-muted">Coverage Period:</span><span className="fw-bold">{pendingStaffData?.periodLabel || selectedPeriod?.label || '1 Month'}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span className="text-muted">Premium per Staff/Month:</span><span className="fw-bold">₦{DEFAULT_MINIMUM_PREMIUM.toLocaleString()}</span></div>
                    <hr /><div className="d-flex justify-content-between"><span className="fw-semibold">Total Premium:</span><span className="fw-bold text-success fs-5">{formatCurrency(paymentAmount)}</span></div>
                  </div>
                </div>
                <button className="btn btn-success btn-lg w-100 mb-3" onClick={async () => {
                  if (!paystackLoaded) { toast.warning('Payment gateway loading...'); return; }
                  const isImportFlow = pendingStaffData?.file; const isBulkRenewal = pendingStaffData?.isBulkRenewal;
                  
                  // ✅ FIXED EMAIL LOGIC WITH FALLBACK CHAIN
                  const emailForPayment = isImportFlow 
                    ? (companyInfo?.email || 'import@company.com') 
                    : pendingStaffData?.email || staffForm?.email || companyInfo?.email;

                  // Debug logging (remove in production)
                  console.log('🔍 Payment Email:', { isImportFlow, isBulkRenewal, emailForPayment });

                  // ✅ VALIDATE EMAIL BEFORE PROCEEDING
                  if (!emailForPayment || !emailForPayment.includes('@')) {
                    toast.error('A valid email is required for payment processing');
                    return;
                  }
                  
                  try {
                    await openPaystackPopup(paymentReference, paymentAmount, emailForPayment, async (ref) => {
                      if (isBulkRenewal) await executeBulkRenewal(ref.reference, ref.tranx_id);
                      else if (isImportFlow) await handleImportAfterPayment(ref);
                      else await handlePaystackSuccess(ref);
                    }, handlePaystackClose);
                  } catch (err) { if (err.message !== 'Payment cancelled') toast.error(err.message || 'Payment failed'); }
                }} disabled={isProcessingPayment || !paystackLoaded} style={{ opacity: (isProcessingPayment || !paystackLoaded) ? 0.7 : 1 }}>
                  {isProcessingPayment ? <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</> : <><i className="fas fa-credit-card me-2"></i>Pay {formatCurrency(paymentAmount)} Now</>}
                </button>
                <small className="text-muted d-block"><i className="fas fa-lock me-1"></i>Secure & encrypted payment</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Bulk Renewal Modal - ENHANCED UX */}
      {showBulkRenewalModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header border-bottom bg-warning bg-opacity-10">
                <h5 className="modal-title text-warning"><i className="fas fa-sync-alt me-2"></i>🔄 Bulk Renew HMO Coverage</h5>
                <button type="button" className="btn-close" onClick={() => setShowBulkRenewalModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Renewal Mode Selection */}
                <div className="mb-4">
                  <label className="form-label small fw-semibold">Select Staff to Renew</label>
                  <div className="btn-group w-100" role="group">
                    <button type="button" className={`btn btn-sm ${renewalSelection.mode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => handleRenewalModeChange('all')}>All Staff ({staffList.length})</button>
                    <button type="button" className={`btn btn-sm ${renewalSelection.mode === 'expired' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => handleRenewalModeChange('expired')}>
                      <i className="fas fa-exclamation-triangle me-1"></i>Expired Only ({staffStats.expiredCount})
                    </button>
                    <button type="button" className={`btn btn-sm ${renewalSelection.mode === 'expiring' ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => handleRenewalModeChange('expiring')}>Expiring Soon ({staffStats.expiringSoonCount})</button>
                  </div>
                </div>

                {/* Selected Staff List */}
                <div className="mb-4">
                  <label className="form-label small fw-semibold">Selected Staff ({renewalSelection.selectedStaffIds.length})</label>
                  <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {renewalSelection.selectedStaffIds.length === 0 ? (
                      <p className="text-muted small mb-0">No staff selected. Choose a filter above or select individually from the table.</p>
                    ) : (
                      <ul className="list-unstyled mb-0 small">
                        {staffList.filter(s => renewalSelection.selectedStaffIds.includes(s.id)).slice(0, 10).map(staff => (
                          <li key={staff.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                            <span>{staff.firstName} {staff.lastName} {calculateCoverageStatus(staff.coverageEndDate, staff.coverageDays).isExpired && <span className="text-danger ms-1">❌</span>}</span>
                            <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleStaffSelectionToggle(staff.id)}><i className="fas fa-times"></i></button>
                          </li>
                        ))}
                        {renewalSelection.selectedStaffIds.length > 10 && (<li className="text-muted small py-1">+ {renewalSelection.selectedStaffIds.length - 10} more...</li>)}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Renewal Period Selection */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Renewal Period <span className="text-danger">*</span></label>
                  <div className="position-relative">
                    <div onClick={() => setShowPeriodDropdown(!showPeriodDropdown)} className="form-control" style={{ border: `2px solid ${showPeriodDropdown ? '#F89423' : '#E5E7EB'}`, borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: renewalSelection.renewalPeriod ? '#111827' : '#6b7280' }}>{renewalSelection.renewalPeriod ? renewalSelection.renewalPeriod.label : 'Select renewal period'}</span>
                      <i className={`fas fa-chevron-${showPeriodDropdown ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                    </div>
                    {showPeriodDropdown && (
                      <div className="position-absolute w-100 mt-1 rounded-3 shadow" style={{ backgroundColor: 'white', border: `1px solid #E5E7EB`, zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>
                        {PAYMENT_PERIODS.map((period) => (
                          <div key={period.value} onClick={() => handleRenewalPeriodSelect(period)} className="p-3 period-option" style={{ backgroundColor: renewalSelection.renewalPeriod?.value === period.value ? '#FFF8F0' : 'white', color: renewalSelection.renewalPeriod?.value === period.value ? '#F89423' : '#111827', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                            {period.label} - {formatCurrency(DEFAULT_MINIMUM_PREMIUM * period.months)} per staff
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Premium Summary */}
                {renewalSelection.renewalPeriod && renewalSelection.selectedStaffIds.length > 0 && (
                  <div className="alert alert-info border-0" style={{ backgroundColor: '#f0f9ff' }}>
                    <h6 className="mb-2">💰 Renewal Premium Summary</h6>
                    <div className="row small">
                      <div className="col-6"><span className="text-muted">Staff Count:</span> <span className="fw-semibold ms-2">{renewalSelection.selectedStaffIds.length}</span></div>
                      <div className="col-6"><span className="text-muted">Period:</span> <span className="fw-semibold ms-2">{renewalSelection.renewalPeriod.label}</span></div>
                      <div className="col-6 mt-2"><span className="text-muted">Per Staff/Month:</span> <span className="fw-semibold ms-2">₦{DEFAULT_MINIMUM_PREMIUM.toLocaleString()}</span></div>
                      <div className="col-6 mt-2"><span className="text-muted">Total Premium:</span> <span className="fw-bold ms-2 text-success">{formatCurrency(renewalSelection.renewalPremium)}</span></div>
                    </div>
                  </div>
                )}

                {/* Warning for Expired Renewals */}
                {renewalSelection.mode === 'expired' && renewalSelection.selectedStaffIds.length > 0 && (
                  <div className="alert alert-warning border-0 mt-3 small">
                    <i className="fas fa-info-circle me-1"></i>
                    <strong>Note:</strong> Renewing expired coverage will restore benefits from today's date. Previous expired period cannot be recovered.
                  </div>
                )}
              </div>
              <div className="modal-footer border-top">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkRenewalModal(false)}>Cancel</button>
                <button className="btn btn-warning" onClick={prepareBulkRenewalPayment} disabled={renewalSelection.selectedStaffIds.length === 0 || !renewalSelection.renewalPeriod || !paystackLoaded}>
                  <i className="fas fa-credit-card me-2"></i>Proceed to Payment ({formatCurrency(renewalSelection.renewalPremium)})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Styles */}
      <style>{`
        .admin-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .sidebar { width: 260px; background: white; position: fixed; left: 0; top: 0; height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000; border-right: 1px solid #e5e7eb; }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; }
        .brand-logo { color: #333; font-weight: 700; text-decoration: none; display: flex; align-items: center; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #F89423; padding: 0 20px; margin-bottom: 10px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link { display: flex; align-items: center; padding: 12px 20px; color: #333; text-decoration: none; transition: all 0.3s ease; font-weight: 500; }
        .nav-link:hover, .nav-link.active { background-color: #F89423; color: white; border-left: 4px solid white; }
        .sidebar-footer { margin-top: auto; padding-bottom: 20px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .top-header { background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .header-icon { width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; position: relative; }
        .header-icon:hover { background: #e5e7eb; color: #F89423; }
        .header-icon .badge { position: absolute; top: 5px; right: 5px; background: #F89423; color: white; font-size: 10px; padding: 2px 5px; border-radius: 10px; }
        .user-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, #F89423 0%, #F89423 100%); color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; cursor: pointer; font-size: 14px; }
        .table th { background-color: #f9fafb; border-bottom: 1px solid #e5e7eb; font-weight: 600; font-size: 13px; color: #374151; }
        .table td { vertical-align: middle; border-bottom: 1px solid #f3f4f6; padding: 12px 16px; }
        .badge.bg-purple { background-color: #6f42c1 !important; color: white !important; }
        .modal.fade.show { display: block; }
        .period-dropdown .position-absolute { box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 1000; }
        .period-dropdown .p-3:hover, .period-option:hover { background-color: #FFF8F0 !important; }
        .coverage-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .coverage-card:hover { transform: translateY(-2px); box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
        .table-warning { background-color: #fff3cd !important; }
        .table-danger { background-color: #f8d7da !important; }
        .btn-group .btn { border-radius: 4px !important; }
        .btn-group .btn:not(:last-child) { border-top-right-radius: 0 !important; border-bottom-right-radius: 0 !important; }
        .btn-group .btn:not(:first-child) { border-top-left-radius: 0 !important; border-bottom-left-radius: 0 !important; margin-left: -1px; }
        @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); } 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); } }
        .alert-danger { animation: pulse 2s infinite; }
        @media (max-width: 992px) { .sidebar { transform: translateX(-100%); } .sidebar.sidebar-open { transform: translateX(0); } .main-content { margin-left: 0; } }
        @media (max-width: 768px) { .content-wrapper { padding: 15px; } .top-header { padding: 15px; } }
      `}</style>
    </div>
  );
}

export default CompanyHmoStaff;